import { RawTableCellValue } from "shared/utils/formatValue/types";

export const areArraysEqual = (array1?: any[], array2?: any[]) => {
  if (!array1 || !array2) return false;

  if (array1.length !== array2.length) return false;

  return array1.every((element) => {
    if (!array2.includes(element)) return false;

    return true;
  });
};

export const intersection = (a: any[], b: any[]) => {
  const setA = new Set(a);
  const setB = new Set(b);
  const intersection = new Set([...setA].filter((x) => setB.has(x)));

  return Array.from(intersection);
};

export const excludeNulls = <T = string>(items: (T | null)[]): T[] =>
  items.filter((e): e is Exclude<typeof e, null> => e !== null);

export const areJsonObjectsEqual = (
  obj1?: Object | number | string | null,
  obj2?: Object | number | string | null
): boolean => {
  if (typeof obj1 !== typeof obj2 || Boolean(obj1) !== Boolean(obj2)) {
    return false;
  }

  if (
    (typeof obj1 === "string" && typeof obj2 === "string") ||
    (typeof obj1 === "number" && typeof obj2 === "number")
  ) {
    return obj1 === obj2;
  } else if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;

    return obj1.every((element, index) =>
      areJsonObjectsEqual(element, obj2[index])
    );
  } else if (typeof obj1 === "object" || typeof obj2 === "object") {
    if (Boolean(obj1) !== Boolean(obj2)) {
      return false;
    }

    const obj1Keys = Object.keys(obj1 ?? {});
    const obj2Keys = Object.keys(obj2 ?? {});

    if (obj1Keys.length !== obj2Keys.length) return false;

    return obj1Keys.every((key) =>
      areJsonObjectsEqual((obj1 as any)[key], (obj2 as any)[key])
    );
  }

  return true;
};

export const cloneObject = <T = Record<string, any>>(object: T): T =>
  JSON.parse(JSON.stringify(object));

// The first maxVisible items and the rest; a null limit shows every item.
export const splitVisibleItems = <T>(
  items: T[],
  maxVisible: number | null
): { visible: T[]; hidden: T[] } =>
  maxVisible === null || maxVisible >= items.length
    ? { visible: items, hidden: [] }
    : { visible: items.slice(0, maxVisible), hidden: items.slice(maxVisible) };

// Ordered by name, leaving the list it was given alone: a sort in place would reorder state a component still
// holds.
export const sortByName = <Item extends { name: string }>(
  items: Item[]
): Item[] => [...items].sort((a, b) => a.name.localeCompare(b.name));

const ROW_INDEX_KEY = "__rowIndex";

// Rows read out of a data table carry no identifier of their own, so the grid is given the index under a key the
// data does not already use — a column really called __rowIndex would otherwise have its values replaced by row
// numbers.
export const getRowIdKey = (rows: Record<string, unknown>[]): string => {
  let key = ROW_INDEX_KEY;
  while (rows.some((row) => key in row)) {
    key += "_";
  }

  return key;
};

export const chunk = <T>(items: T[], size: number): T[][] => {
  if (!Number.isSafeInteger(size) || size <= 0) {
    throw new RangeError(`chunk size must be a positive integer, got ${size}`);
  }

  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
};

// Like chunk, but never cuts through a run of neighbouring items that share a key:
// a run is moved whole into the next chunk instead. A run longer than `size` still
// has to be split, since no chunk may exceed what the caller's API accepts.
export const chunkByGroup = <T>(
  items: T[],
  size: number,
  groupKey: (item: T) => string
): T[][] => {
  if (!Number.isSafeInteger(size) || size <= 0) {
    throw new RangeError(`chunk size must be a positive integer, got ${size}`);
  }

  const groups: T[][] = [];
  let previousKey: string | undefined;
  items.forEach((item) => {
    const key = groupKey(item);
    const group = groups[groups.length - 1];

    if (group && key === previousKey) group.push(item);
    else groups.push([item]);

    previousKey = key;
  });

  const chunks: T[][] = [];
  let current: T[] = [];

  groups.forEach((group) => {
    if (current.length > 0 && current.length + group.length > size) {
      chunks.push(current);
      current = [];
    }

    if (group.length > size) {
      chunks.push(...chunk(group, size));

      return;
    }

    current.push(...group);
  });

  if (current.length > 0) chunks.push(current);

  return chunks;
};

// Results keep the input order. One rejection rejects the whole call, so mappers
// that must not fail the batch have to catch their own errors.
export const mapWithConcurrency = async <T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  if (!Number.isSafeInteger(concurrency) || concurrency <= 0) {
    throw new RangeError(
      `concurrency must be a positive integer, got ${concurrency}`
    );
  }

  const results: R[] = [];
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, worker)
  );

  return results;
};

export const getValueByAccessor = (
  accessor: string,
  rowData: Record<string, any>
): RawTableCellValue => {
  // A dot in an accessor normally walks into a nested object, but a column can also be named "vehicle.id" — an
  // uploaded file's header, say — and then the whole thing is one flat key. A key that is really there wins.
  // The row's own keys come from the data, so `hasOwnProperty` is read off the prototype: a column of that name
  // would otherwise shadow the method being called here.
  const has = (o: Record<string, any>, key: string) =>
    Object.prototype.hasOwnProperty.call(o, key);

  if (has(rowData, accessor)) {
    return rowData[accessor] ?? null;
  }

  return accessor
    .split(".")
    .reduce(
      (o, i) =>
        o && has(o, i) && o[i] !== undefined && o[i] !== null ? o[i] : null,
      rowData
    );
};
